import { useEffect, useRef, useState, useCallback } from 'react';

interface UsePixTimerProps {
  duration: number;
  onExpire?: () => void;
}

export const usePixTimer = ({ duration, onExpire }: UsePixTimerProps) => {
  const [timeLeft, setTimeLeft] = useState(duration);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const onExpireRef = useRef(onExpire);

  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  const formatTime = useCallback((seconds: number) => {
    const min = String(Math.floor(seconds / 60)).padStart(2, '0');
    const sec = String(seconds % 60).padStart(2, '0');
    return `${min}:${sec}`;
  }, []);

  const startTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    startTimeRef.current = Date.now();
    setTimeLeft(duration);
    
    intervalRef.current = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(intervalRef.current!);
          intervalRef.current = null;
          onExpireRef.current?.();
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);
  }, [duration]);

  const stopTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    startTimeRef.current = null;
  }, []);

  const resetTimer = useCallback(() => {
    stopTimer();
    setTimeLeft(duration);
  }, [stopTimer, duration]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && startTimeRef.current && intervalRef.current) {
        const now = Date.now();
        const elapsed = Math.floor((now - startTimeRef.current) / 1000);
        const remaining = Math.max(0, duration - elapsed);
        
        setTimeLeft(remaining);
        
  
        if (remaining <= 0) {
          clearInterval(intervalRef.current!);
          intervalRef.current = null;
          onExpireRef.current?.();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [duration]);

  return {
    timeLeft,
    formattedTime: formatTime(timeLeft),
    startTimer,
    stopTimer,
    resetTimer,
    isRunning: intervalRef.current !== null,
  };
}; 